<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left"><h1 class="page-title">{{ isEdit ? '編輯預約' : '新增預約' }}</h1></div>
      <div class="header-actions">
        <button
          v-if="canCancel"
          type="button"
          class="btn btn-danger"
          :disabled="loading || saving || cancelling"
          @click="cancelReservation"
        >
          {{ cancelling ? '取消中...' : '取消預約' }}
        </button>
        <button type="button" class="btn btn-secondary" :disabled="saving || cancelling" @click="goBack">取消</button>
        <button
          v-if="isEdit || currentStep === 3"
          type="button"
          class="btn btn-primary"
          :disabled="loading || saving || cancelling || memberLookupLoading || !canEdit"
          @click="save"
        >
          {{ saving ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
      <form v-else class="form-container salon-form-page" @submit.prevent="save">
        <div v-if="isEdit && !canEdit" class="alert alert-warning">
          此預約目前為「{{ statusLabel(originalStatus) }}」，不可再修改日期、時間或設計師。
        </div>

        <div v-if="!isEdit" class="reservation-steps">
          <div
            v-for="step in steps"
            :key="step.number"
            class="reservation-step"
            :class="{ active: currentStep === step.number, completed: currentStep > step.number }"
          >
            <span>{{ step.number }}</span>
            <small>{{ step.label }}</small>
          </div>
        </div>

        <div v-show="isEdit || currentStep === 1" class="form-section">
          <h2 class="section-title">{{ isEdit ? '門市與設計師' : '步驟 1：選擇門市與設計師' }}</h2>
          <div class="form-group">
            <label class="form-label required-label">門市</label>
            <div class="selection-card-grid">
              <button
                v-for="store in stores"
                :key="store.id"
                type="button"
                class="selection-card store-card"
                :class="{ selected: form.storeId === store.id }"
                :disabled="!canEdit"
                @click="selectStore(store.id)"
              >
                <i class="ri-store-2-line"></i>
                <strong>{{ store.storeName }}</strong>
              </button>
            </div>
            <div v-if="!stores.length" class="empty-state">目前沒有開放美髮預約的門市。</div>
          </div>
          <div class="form-group">
            <label class="form-label">設計師</label>
            <div v-if="form.storeId" class="selection-card-grid stylist-card-grid">
              <button
                v-if="!isEdit"
                type="button"
                class="selection-card stylist-card"
                :class="{ selected: !form.stylistId }"
                :disabled="!canEdit"
                @click="selectStylist('')"
              >
                <span class="stylist-avatar fallback"><i class="ri-user-line"></i></span>
                <strong>不指定</strong>
              </button>
              <button
                v-for="stylist in stylists"
                :key="stylist.id"
                type="button"
                class="selection-card stylist-card"
                :class="{ selected: form.stylistId === stylist.id }"
                :disabled="!canEdit"
                @click="selectStylist(stylist.id)"
              >
                <img
                  v-if="stylist.avatarUrl"
                  class="stylist-avatar"
                  :src="stylist.avatarUrl"
                  :alt="stylist.displayName"
                />
                <span v-else class="stylist-avatar fallback">{{ stylistInitial(stylist) }}</span>
                <strong>{{ stylist.displayName }}</strong>
              </button>
            </div>
            <span v-if="form.storeId && !stylists.length" class="form-hint">此門市目前沒有可服務的設計師。</span>
          </div>
        </div>

        <div v-show="isEdit || currentStep === 2" class="form-section">
          <h2 class="section-title">{{ isEdit ? '服務、預約日期與時段' : '步驟 2：選擇服務、日期與時段' }}</h2>
          <p class="form-label required-label">服務項目</p>
          <div class="service-options">
            <label
              v-for="service in services"
              :key="service.id"
              class="service-option"
              :class="{ selected: form.serviceIds.includes(service.id) }"
            >
              <input
                v-model="form.serviceIds"
                type="checkbox"
                :value="service.id"
                :disabled="!canEdit || !form.storeId"
                @change="handleServiceSelectionChange"
              />
              <span><strong>{{ service.name }}</strong><small>{{ service.durationMinutes }} 分鐘</small></span>
            </label>
          </div>
          <div v-if="!services.length" class="empty-state">目前沒有可選擇的服務項目。</div>
          <template v-if="form.serviceIds.length">
            <p v-if="!isEdit" class="form-label required-label">日期</p>
            <div v-if="isEdit" class="form-group">
              <label class="form-label required-label">日期</label>
              <input v-model="form.appointmentDate" type="date" class="form-control" :disabled="!canEdit" required />
            </div>
            <div v-else-if="findingDate" class="date-finding">
              <div class="spinner"></div><span>正在尋找最近尚有預約餘額的日期...</span>
            </div>
            <div v-else-if="availableDates.length">
              <p class="form-hint availability-range">
                此門市最遠可預約至
                {{ displayDate(maxBookingDate) }}（{{ maxFutureBookingDays }} 天）。
              </p>
              <div class="availability-calendar">
                <div class="calendar-toolbar">
                  <button type="button" class="btn btn-sm btn-secondary" :disabled="!canGoPreviousWeek" @click="changeCalendarWeek(-1)">
                    <i class="ri-arrow-left-s-line"></i>
                  </button>
                  <strong>{{ calendarWeekLabel }}</strong>
                  <button type="button" class="btn btn-sm btn-secondary" :disabled="!canGoNextWeek" @click="changeCalendarWeek(1)">
                    <i class="ri-arrow-right-s-line"></i>
                  </button>
                </div>
                <div class="calendar-weekdays">
                  <span v-for="weekday in calendarWeekdays" :key="weekday">{{ weekday }}</span>
                </div>
                <div class="calendar-days">
                  <button
                    v-for="day in calendarDays"
                    :key="day.key"
                    type="button"
                    class="calendar-day"
                    :class="{
                      available: day.available,
                      unavailable: day.date && !day.available,
                      selected: day.date === form.appointmentDate
                    }"
                    :disabled="!day.available"
                    @click="selectAppointmentDate(day)"
                  >
                    <span v-if="day.date">{{ day.day }}</span>
                  </button>
                </div>
                <div class="calendar-legend">
                  <span><i class="legend-color available"></i>可預約</span>
                  <span><i class="legend-color unavailable"></i>不可預約</span>
                  <span><i class="legend-color selected"></i>已選擇</span>
                </div>
              </div>
              <button type="button" class="btn btn-secondary refresh-dates" @click="refreshAvailableDates">
                依所選服務重新查詢
              </button>
            </div>
            <div v-else class="empty-state">目前找不到可預約日期。</div>
            <div class="time-selection">
              <h3 class="form-label required-label">選擇時段</h3>
              <div class="time-slot-options">
                <label
                  v-for="slot in availability"
                  :key="slot.startTime"
                  class="time-slot-option"
                  :class="{ selected: form.startTime === slot.startTime }"
                >
                  <input v-model="form.startTime" type="radio" :value="slot.startTime" :disabled="!canEdit" />
                  <span>{{ slot.startTime }}</span>
                </label>
              </div>
              <div v-if="form.appointmentDate && !availability.length" class="empty-state">
                此日期目前沒有可預約時段。
              </div>
            </div>
          </template>
        </div>

        <div v-show="isEdit || currentStep === 3" class="form-section">
          <h2 class="section-title">{{ isEdit ? '顧客資料' : '步驟 3：填寫顧客資料' }}</h2>
          <div v-if="isEdit" class="form-group">
            <label class="form-label">預約編號</label>
            <input :value="reservationNumber" class="form-control" disabled />
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label required-label">手機</label>
              <div class="phone-lookup-field">
                <input
                  v-model.trim="form.contactPhone"
                  class="form-control"
                  maxlength="30"
                  :disabled="!canEdit"
                  required
                  @input="handlePhoneInput"
                  @blur="lookupMemberByPhone"
                />
                <span v-if="memberLookupLoading" class="form-hint">會員查詢中...</span>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label required-label">聯絡姓名</label>
              <input
                v-model.trim="form.contactName"
                class="form-control"
                maxlength="100"
                :disabled="!canEdit"
                required
              />
            </div>
          </div>
          <div v-if="matchedMember" class="matched-member-card">
            <i class="ri-user-heart-line"></i>
            <div>
              <strong>{{ matchedMember.name }}</strong>
              <span>
                會員編號：{{ matchedMember.memberCode || '-' }}
                <template v-if="matchedMember.tierName">・{{ matchedMember.tierName }}</template>
              </span>
              <span v-if="matchedMember.email">{{ matchedMember.email }}</span>
            </div>
          </div>
          <p v-else-if="memberLookupDone && form.contactPhone" class="form-hint">
            此電話未找到會員，將以訪客資料建立預約。
          </p>
          <label v-if="matchedMember && !isEdit" class="notification-option">
            <input
              v-model="form.sendMemberNotification"
              type="checkbox"
              :disabled="!matchedMember.canReceiveLinePush"
            />
            <span>
              完成預約後發送 LINE 推播
              <small v-if="!matchedMember.canReceiveLinePush">此會員尚未綁定可接收推播的 LINE 好友</small>
            </span>
          </label>
          <div class="form-group">
            <label class="form-label">備註</label>
            <textarea v-model.trim="form.notes" class="form-control" rows="3" :disabled="!canEdit"></textarea>
          </div>
        </div>

        <div v-if="!isEdit" class="wizard-actions">
          <button
            v-if="currentStep > 1"
            type="button"
            class="btn btn-secondary"
            :disabled="findingDate || saving"
            @click="previousStep"
          >
            上一步
          </button>
          <button
            v-if="currentStep < 3"
            type="button"
            class="btn btn-primary"
            :disabled="findingDate || saving || (currentStep === 1 && !stores.length)"
            @click="nextStep"
          >
            {{ findingDate ? '查詢中...' : '下一步' }}
          </button>
          <button
            v-if="currentStep === 3"
            type="button"
            class="btn btn-primary"
            :disabled="saving || memberLookupLoading || !canEdit"
            @click="save"
          >
            {{ saving ? '儲存中...' : '完成' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { memberService } from '@/api/modules/members.js';
import { salonDateOnly } from '@/utils/salonDate.js';
import { fireSwalDeferred, showError, showSuccess } from '@/utils/swal.js';

const emptyForm = () => ({
  storeId: '', stylistId: '', memberId: null, serviceIds: [],
  appointmentDate: '', startTime: '', contactName: '', contactPhone: '', notes: '',
  sendMemberNotification: false
});

export default {
  name: 'SalonReservationForm',
  data() {
    return {
      loading: false, saving: false, cancelling: false, stores: [], stylists: [], services: [],
      availability: [], reservationNumber: '', originalStatus: '', form: emptyForm(),
      matchedMember: null, memberLookupLoading: false, memberLookupDone: false,
      currentStep: 1, findingDate: false, availableDates: [],
      maxBookingDate: '', maxFutureBookingDays: 0, calendarWeekStart: '',
      calendarWeekdays: ['日', '一', '二', '三', '四', '五', '六'],
      steps: [
        { number: 1, label: '門市與設計師' },
        { number: 2, label: '服務、日期與時段' },
        { number: 3, label: '顧客資料' }
      ]
    };
  },
  computed: {
    isEdit() { return Boolean(this.$route.params.id); },
    canEdit() { return !this.isEdit || ['confirmed', 'checked_in', 'in_service'].includes(this.originalStatus); },
    canCancel() {
      return this.isEdit && !['completed', 'cancelled'].includes(this.originalStatus);
    },
    calendarWeekLabel() {
      if (!this.calendarWeekStart) return '';
      const start = new Date(`${this.calendarWeekStart}T00:00:00Z`);
      const end = new Date(start);
      end.setUTCDate(end.getUTCDate() + 6);
      const format = value => `${value.getUTCMonth() + 1}/${value.getUTCDate()}`;
      return `${start.getUTCFullYear()} 年 ${format(start)}－${format(end)}`;
    },
    canGoPreviousWeek() {
      return Boolean(this.calendarWeekStart)
        && this.calendarWeekStart > this.weekStartOf(this.dateOnly(new Date()));
    },
    canGoNextWeek() {
      return Boolean(this.calendarWeekStart && this.maxBookingDate)
        && this.calendarWeekStart < this.weekStartOf(this.maxBookingDate);
    },
    calendarDays() {
      if (!this.calendarWeekStart) return [];
      const start = new Date(`${this.calendarWeekStart}T00:00:00Z`);
      const today = this.dateOnly(new Date());
      const availabilityByDate = new Map(this.availableDates.map(item => [item.date, item]));
      return Array.from({ length: 7 }, (_, index) => {
        const current = new Date(start);
        current.setUTCDate(current.getUTCDate() + index);
        const date = current.toISOString().slice(0, 10);
        const availability = availabilityByDate.get(date);
        return {
          key: date,
          date,
          day: `${current.getUTCMonth() + 1}/${current.getUTCDate()}`,
          available: Boolean(availability) && date >= today && date <= this.maxBookingDate
        };
      });
    }
  },
  async mounted() {
    this.loading = true;
    try {
      const [stores, services, settings] = await Promise.all([
        storeService.getStores({ page: 1, pageSize: 100, status: 'active' }),
        salonService.listServices({ page: 1, pageSize: 100, status: 'active' }),
        salonService.listSettings()
      ]);
      const activeStores = stores?.data?.items || [];
      const openStoreIds = new Set(
        (settings?.data || [])
          .filter(item => item.bookingEnabled)
          .map(item => item.storeId)
      );
      const openStores = activeStores.filter(store => openStoreIds.has(store.id));
      this.stores = openStores;
      this.services = services?.data?.items || [];
      if (this.isEdit) {
        await this.loadReservation();
        if (this.form.storeId && !this.stores.some(store => store.id === this.form.storeId)) {
          const currentStore = activeStores.find(store => store.id === this.form.storeId);
          if (currentStore) this.stores = [currentStore, ...this.stores];
        }
      } else if (this.stores.length === 1) {
        this.form.storeId = this.stores[0].id;
        await this.handleStoreChange();
      }
    } catch (error) {
      await this.handleError(error, '預約資料載入失敗');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadReservation() {
      const response = await salonService.getReservation(this.$route.params.id);
      const item = response.data;
      this.reservationNumber = item.reservationNumber;
      this.originalStatus = item.status;
      this.form = {
        ...emptyForm(),
        storeId: item.storeId,
        stylistId: item.stylistId,
        memberId: item.memberId,
        serviceIds: (item.services || []).map(service => service.serviceId),
        appointmentDate: this.dateOnly(item.appointmentDate),
        startTime: this.shortTime(item.startTime),
        contactName: item.contactName || '',
        contactPhone: item.contactPhone,
        notes: item.notes || ''
      };
      await this.loadStylists(this.form.storeId);
      await this.loadServices(this.form.stylistId);
      this.availability = [{
        startTime: this.shortTime(item.startTime),
        endTime: this.shortTime(item.endTime)
      }];
    },
    async loadStylists(storeId) {
      if (!storeId) {
        this.stylists = [];
        return;
      }
      const response = await salonService.listStylists({
        page: 1, pageSize: 100, status: 'active', storeId
      });
      if (this.form.storeId !== storeId) return;
      this.stylists = response?.data?.items || [];
    },
    async loadServices(stylistId) {
      const response = await salonService.listServices({
        page: 1, pageSize: 100, status: 'active',
        ...(stylistId ? { stylistId } : {})
      });
      if (this.form.stylistId !== stylistId) return;
      this.services = response?.data?.items || [];
    },
    async selectStore(storeId) {
      if (this.form.storeId === storeId) return;
      this.form.storeId = storeId;
      await this.handleStoreChange();
    },
    async selectStylist(stylistId) {
      if (this.form.stylistId === stylistId) return;
      this.form.stylistId = stylistId;
      await this.handleStylistChange();
    },
    async handleStoreChange() {
      this.form.stylistId = '';
      this.form.serviceIds = [];
      this.form.appointmentDate = '';
      this.form.startTime = '';
      this.availability = [];
      this.availableDates = [];
      this.maxBookingDate = '';
      this.maxFutureBookingDays = 0;
      this.calendarWeekStart = '';
      try {
        await Promise.all([
          this.loadStylists(this.form.storeId),
          this.loadServices('')
        ]);
      } catch (error) {
        await this.handleError(error, '門市服務資料載入失敗');
      }
    },
    async handleStylistChange() {
      this.form.serviceIds = [];
      this.form.appointmentDate = '';
      this.form.startTime = '';
      this.availability = [];
      this.availableDates = [];
      this.maxBookingDate = '';
      this.maxFutureBookingDays = 0;
      this.calendarWeekStart = '';
      try {
        await this.loadServices(this.form.stylistId);
      } catch (error) {
        await this.handleError(error, '服務項目載入失敗');
      }
    },
    async findAvailableDates(useSelectedServices) {
      if (!this.form.storeId) return false;
      this.findingDate = true;
      this.form.startTime = '';
      this.availability = [];
      try {
        const response = await salonService.getAvailableDates({
          storeId: this.form.storeId,
          stylistId: this.form.stylistId || null,
          serviceIds: useSelectedServices ? this.form.serviceIds : [],
          startDate: this.dateOnly(new Date()),
          limit: 730
        });
        const dates = (response.data.dates || []).map(item => ({
          ...item,
          date: this.dateOnly(item.date)
        }));
        this.availableDates = dates;
        this.maxBookingDate = this.dateOnly(response.data.maxBookingDate);
        this.maxFutureBookingDays = Number(response.data.maxFutureBookingDays || 0);
        if (!this.availableDates.length) {
          this.form.appointmentDate = '';
          await showError(
            '目前無可預約日期',
            useSelectedServices
              ? '此設計師與服務組合目前沒有可預約餘額。'
              : '此設計師目前沒有可預約餘額。'
          );
          return false;
        }
        const selectedStillAvailable = this.availableDates.some(item => (
          item.date === this.form.appointmentDate
        ));
        if (!selectedStillAvailable) {
          this.form.appointmentDate = this.availableDates[0].date;
        }
        this.calendarWeekStart = this.weekStartOf(this.form.appointmentDate);
        return true;
      } catch (error) {
        await this.handleError(error, '最近可預約日期查詢失敗');
        return false;
      } finally {
        this.findingDate = false;
      }
    },
    changeCalendarWeek(offset) {
      if (!this.calendarWeekStart) return;
      const date = new Date(`${this.calendarWeekStart}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() + (offset * 7));
      this.calendarWeekStart = date.toISOString().slice(0, 10);
    },
    async selectAppointmentDate(day) {
      if (!day.available) return;
      this.form.appointmentDate = day.date;
      if (this.form.serviceIds.length) await this.loadAvailability(true);
    },
    async handleServiceSelectionChange() {
      this.form.startTime = '';
      this.availability = [];
      const useSelectedServices = this.form.serviceIds.length > 0;
      if (await this.findAvailableDates(useSelectedServices) && useSelectedServices) {
        await this.loadAvailability(true);
      }
    },
    async refreshAvailableDates() {
      const useSelectedServices = this.form.serviceIds.length > 0;
      if (await this.findAvailableDates(useSelectedServices) && useSelectedServices) {
        await this.loadAvailability(true);
      }
    },
    stepValidationErrors(step) {
      const errors = [];
      if (step === 1) {
        if (!this.form.storeId) {
          errors.push('門市：尚未選擇');
        } else if (!this.isEdit && !this.stores.some(store => store.id === this.form.storeId)) {
          errors.push('門市：所選門市目前未開放預約');
        }
        if (this.form.stylistId
          && !this.isEdit
          && !this.stylists.some(stylist => stylist.id === this.form.stylistId)) {
          errors.push('設計師：所選設計師目前無法預約');
        }
      }
      if (step === 2) {
        if (!this.form.serviceIds.length) {
          errors.push('服務項目：至少選擇一項');
        } else if (!this.isEdit && this.form.serviceIds.some(
          id => !this.services.some(service => service.id === id)
        )) {
          errors.push('服務項目：包含目前無法提供的服務');
        }
        if (!this.form.appointmentDate) {
          errors.push('預約日期：尚未選擇');
        } else if (!this.isEdit && !this.availableDates.some(
          item => item.date === this.form.appointmentDate
        )) {
          errors.push('預約日期：所選日期目前無法預約');
        }
        if (!this.form.startTime) {
          errors.push('預約時段：尚未選擇');
        } else if (!this.isEdit && !this.availability.some(
          slot => slot.startTime === this.form.startTime
        )) {
          errors.push('預約時段：所選時段目前無法預約');
        }
      }
      if (step === 3) {
        if (!this.form.contactPhone) {
          errors.push('手機：尚未輸入');
        } else if (this.form.contactPhone.replace(/\D/g, '').length < 6) {
          errors.push('手機：格式不完整，至少需要 6 位數字');
        }
        if (!this.form.contactName?.trim()) {
          errors.push('聯絡姓名：尚未輸入');
        }
        if (this.memberLookupLoading) {
          errors.push('會員資料：仍在查詢中');
        }
      }
      return errors;
    },
    async validateStep(step) {
      const errors = this.stepValidationErrors(step);
      if (!errors.length) return true;
      await showError(
        `步驟 ${step} 資料未完整`,
        `請補齊或修正以下資料：\n• ${errors.join('\n• ')}`
      );
      return false;
    },
    async nextStep() {
      if (this.currentStep === 1) {
        if (!await this.validateStep(1)) return;
        const selectedDate = this.form.appointmentDate;
        const selectedTime = this.form.startTime;
        const useSelectedServices = this.form.serviceIds.length > 0;
        if (await this.findAvailableDates(useSelectedServices)) {
          if (useSelectedServices) {
            await this.loadAvailability(true);
            if (this.form.appointmentDate === selectedDate
              && this.availability.some(slot => slot.startTime === selectedTime)) {
              this.form.startTime = selectedTime;
            }
          }
          this.currentStep = 2;
        }
        return;
      }
      if (this.currentStep === 2) {
        if (!await this.validateStep(2)) return;
        this.currentStep = 3;
        return;
      }
    },
    previousStep() {
      if (this.currentStep > 1) this.currentStep -= 1;
    },
    async loadAvailability(useSelectedServices = this.form.serviceIds.length > 0) {
      if (!this.form.storeId || !this.form.appointmentDate) {
        await showError('資料不足', '請選擇門市與日期。');
        return;
      }
      try {
        const response = await salonService.getAvailability({
          storeId: this.form.storeId,
          stylistId: this.form.stylistId || null,
          serviceIds: useSelectedServices ? this.form.serviceIds : [],
          date: this.form.appointmentDate
        });
        this.availability = response.data.slots || [];
        if (!this.availability.some(slot => slot.startTime === this.form.startTime)) {
          this.form.startTime = '';
        }
      } catch (error) {
        await this.handleError(error, '時段查詢失敗');
      }
    },
    normalizedMemberPhone(value = this.form.contactPhone) {
      const digits = String(value || '').replace(/\D/g, '');
      if (digits.startsWith('886') && digits.length === 12) return `0${digits.slice(3)}`;
      return digits;
    },
    handlePhoneInput() {
      const currentPhone = this.normalizedMemberPhone();
      const matchedPhone = this.normalizedMemberPhone(this.matchedMember?.phone);
      if (!this.matchedMember || currentPhone !== matchedPhone) {
        this.matchedMember = null;
        this.form.memberId = null;
        this.form.sendMemberNotification = false;
      }
      this.memberLookupDone = false;
    },
    async lookupMemberByPhone() {
      const phone = this.normalizedMemberPhone();
      if (!/^09\d{8}$/.test(phone)) return true;
      if (this.memberLookupLoading) return false;
      if (this.matchedMember && this.normalizedMemberPhone(this.matchedMember.phone) === phone) {
        this.memberLookupDone = true;
        return true;
      }
      this.memberLookupLoading = true;
      this.memberLookupDone = false;
      try {
        const response = await memberService.getMembersByIdentifiers([phone]);
        if (this.normalizedMemberPhone() !== phone) return;
        const members = response?.data?.items || response?.data || [];
        const member = members.find(item => this.normalizedMemberPhone(item.phone) === phone) || null;
        this.matchedMember = member;
        this.form.memberId = member?.id || null;
        this.form.sendMemberNotification = false;
        if (member?.name) this.form.contactName = member.name;
        this.memberLookupDone = true;
        return true;
      } catch (error) {
        if (this.normalizedMemberPhone() === phone) {
          this.matchedMember = null;
          this.form.memberId = null;
          this.form.sendMemberNotification = false;
          this.memberLookupDone = true;
          await this.handleError(error, '會員查詢失敗');
        }
        return false;
      } finally {
        this.memberLookupLoading = false;
      }
    },
    async save() {
      if (this.saving || this.memberLookupLoading || !this.canEdit) return;
      if (!this.isEdit
        && this.form.contactPhone?.replace(/\D/g, '').length >= 6
        && !this.memberLookupDone) {
        if (!await this.lookupMemberByPhone()) return;
      }
      for (const step of [1, 2, 3]) {
        const errors = this.stepValidationErrors(step);
        if (errors.length) {
          if (!this.isEdit) this.currentStep = step;
          await this.validateStep(step);
          return;
        }
      }

      const selectedDate = this.form.appointmentDate;
      const selectedTime = this.form.startTime;
      const datesAvailable = await this.findAvailableDates(true);
      if (datesAvailable && this.form.appointmentDate === selectedDate) {
        await this.loadAvailability(true);
      }
      const selectionStillAvailable = datesAvailable
        && this.form.appointmentDate === selectedDate
        && this.availability.some(slot => slot.startTime === selectedTime);
      if (!selectionStillAvailable) {
        this.form.startTime = '';
        const dateStillAvailable = datesAvailable && this.form.appointmentDate === selectedDate;
        this.currentStep = 2;
        await showError(
          dateStillAvailable ? '請重新選擇時段' : '請重新選擇日期',
          dateStillAvailable
            ? '原時段目前已無法預約，請重新選擇。'
            : '原日期目前已無法預約，請重新選擇。'
        );
        return;
      }
      this.form.startTime = selectedTime;

      this.saving = true;
      try {
        let savedReservation = null;
        if (this.isEdit) {
          savedReservation = await salonService.updateReservation(this.$route.params.id, {
            storeId: this.form.storeId,
            stylistId: this.form.stylistId,
            serviceIds: this.form.serviceIds,
            appointmentDate: this.form.appointmentDate,
            startTime: this.form.startTime,
            contactName: this.form.contactName || null,
            contactPhone: this.form.contactPhone,
            notes: this.form.notes || null
          });
        } else {
          savedReservation = await salonService.createReservation({
            ...this.form,
            stylistId: this.form.stylistId || null,
            contactName: this.form.contactName || null,
            notes: this.form.notes || null,
            idempotencyKey: crypto.randomUUID()
          });
        }
        const notification = savedReservation?.data?.memberNotification;
        const successMessage = this.form.sendMemberNotification && !notification?.sent
          ? '預約已儲存，但 LINE 推播未送達。'
          : '預約已儲存';
        await showSuccess(this.isEdit ? '更新成功' : '建立成功', successMessage);
        this.goBack();
      } catch (error) {
        await this.handleError(error, '預約儲存失敗');
      } finally {
        this.saving = false;
      }
    },
    async cancelReservation() {
      if (this.cancelling || this.saving || !this.canCancel) return;
      const result = await fireSwalDeferred({
        icon: 'warning',
        title: '取消預約',
        text: '取消後此預約不再占用時段。',
        input: 'textarea',
        inputLabel: '取消原因',
        inputPlaceholder: '請填寫取消原因',
        inputAttributes: { maxlength: 500 },
        inputValidator: value => (value && value.trim() ? undefined : '請填寫取消原因'),
        showCancelButton: true,
        confirmButtonText: '確認取消',
        cancelButtonText: '返回',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        reverseButtons: true
      });
      if (!result.isConfirmed) return;
      this.cancelling = true;
      try {
        await salonService.cancelReservation(this.$route.params.id, result.value.trim());
        await showSuccess('預約已取消', '此預約已不再占用時段。');
        this.goBack();
      } catch (error) {
        await this.handleError(error, '預約取消失敗');
      } finally {
        this.cancelling = false;
      }
    },
    statusLabel(status) {
      return {
        confirmed: '已確認', checked_in: '已報到', in_service: '服務中',
        completed: '已完成', cancelled: '已取消', no_show: '未到'
      }[status] || status;
    },
    dateOnly(value) { return salonDateOnly(value); },
    displayDate(value) {
      if (!value) return '-';
      return new Intl.DateTimeFormat('zh-TW', {
        year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
        timeZone: 'Asia/Taipei'
      }).format(new Date(`${value}T00:00:00+08:00`));
    },
    shortTime(value) { return value ? String(value).slice(0, 5) : ''; },
    stylistInitial(stylist) {
      return String(stylist.displayName || stylist.internalName || '?').trim().slice(0, 1);
    },
    weekStartOf(value) {
      const date = new Date(`${this.dateOnly(value)}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() - date.getUTCDay());
      return date.toISOString().slice(0, 10);
    },
    goBack() { this.$router.push({ name: 'SalonOverview' }); },
    async handleError(error, fallback) {
      await showError(fallback, error.response?.data?.message || error.message || fallback);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';
.salon-form-page { max-width: 1000px; margin: 0 auto; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.reservation-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 20px;
}
.reservation-step {
  align-items: center;
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
}
.reservation-step::before {
  background: #dbe3ec;
  content: '';
  height: 2px;
  left: -50%;
  position: absolute;
  right: 50%;
  top: 16px;
}
.reservation-step:first-child::before { display: none; }
.reservation-step > span {
  align-items: center;
  background: #e2e8f0;
  border-radius: 50%;
  color: #64748b;
  display: flex;
  font-weight: 700;
  height: 32px;
  justify-content: center;
  position: relative;
  width: 32px;
  z-index: 1;
}
.reservation-step.active,
.reservation-step.completed { color: #1d4ed8; }
.reservation-step.active > span,
.reservation-step.completed > span { background: #2563eb; color: #fff; }
.reservation-step.completed::before { background: #2563eb; }
.selection-card-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}
.selection-card {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe3ec;
  border-radius: 10px;
  color: #334155;
  cursor: pointer;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  min-height: 64px;
  padding: 12px 14px;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.selection-card:hover { border-color: #93c5fd; }
.selection-card.selected {
  background: #eff6ff;
  border-color: #2563eb;
  box-shadow: 0 0 0 1px #2563eb;
  color: #1d4ed8;
}
.selection-card:disabled { cursor: not-allowed; opacity: 0.65; }
.store-card > i { font-size: 24px; }
.stylist-card-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
.stylist-card { flex-direction: column; justify-content: center; text-align: center; }
.stylist-avatar {
  background: #e2e8f0;
  border-radius: 50%;
  height: 54px;
  object-fit: cover;
  width: 54px;
}
.stylist-avatar.fallback {
  align-items: center;
  color: #475569;
  display: flex;
  font-size: 20px;
  font-weight: 700;
  justify-content: center;
}
.date-finding {
  align-items: center;
  color: #64748b;
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 28px;
}
.service-options,
.time-slot-options { display: flex; flex-wrap: wrap; gap: 10px; }
.availability-range { margin-bottom: 12px; }
.availability-calendar {
  border: 1px solid #dbe3ec;
  border-radius: 10px;
  max-width: 760px;
  overflow: hidden;
}
.calendar-toolbar {
  align-items: center;
  background: #f8fafc;
  display: flex;
  justify-content: space-between;
  padding: 12px;
}
.calendar-weekdays,
.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}
.calendar-weekdays span {
  background: #fff;
  color: #64748b;
  font-size: 13px;
  padding: 8px 4px;
  text-align: center;
}
.calendar-weekdays span:first-child { color: #dc2626; }
.calendar-weekdays span:last-child { color: #16a34a; }
.calendar-day {
  align-items: center;
  background: #fff;
  border: 0;
  border-right: 1px solid #e5e7eb;
  border-top: 1px solid #e5e7eb;
  display: grid;
  gap: 2px;
  min-height: 64px;
  padding: 7px 4px;
  text-align: center;
}
.calendar-day:nth-child(7n) { border-right: 0; }
.calendar-day.available { background: #dcfce7; color: #166534; cursor: pointer; }
.calendar-day.available:hover { background: #bbf7d0; }
.calendar-day.unavailable { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
.calendar-day.selected { background: #2563eb; color: #fff; }
.calendar-day:disabled { opacity: 1; }
.calendar-legend { display: flex; flex-wrap: wrap; gap: 14px; padding: 11px 12px; }
.calendar-legend span { align-items: center; display: flex; font-size: 13px; gap: 6px; }
.legend-color { border-radius: 3px; display: inline-block; height: 14px; width: 14px; }
.legend-color.available { background: #dcfce7; border: 1px solid #86efac; }
.legend-color.unavailable { background: #f1f5f9; border: 1px solid #cbd5e1; }
.legend-color.selected { background: #2563eb; }
.refresh-dates { margin-top: 14px; }
.service-option,
.time-slot-option {
  align-items: center;
  border: 1px solid #dbe3ec;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  gap: 9px;
  margin: 0;
  padding: 11px 14px;
}
.service-option > span { display: grid; gap: 2px; }
.service-option small { color: #64748b; }
.service-option.selected { background: #eff6ff; border-color: #2563eb; }
.time-slot-option.selected { background: #eff6ff; border-color: #2563eb; color: #1d4ed8; }
.phone-lookup-field { display: grid; gap: 5px; }
.matched-member-card {
  align-items: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding: 14px;
}
.matched-member-card > i { color: #2563eb; font-size: 28px; }
.matched-member-card > div { display: grid; gap: 3px; }
.matched-member-card span { color: #64748b; font-size: 13px; }
.notification-option {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #dbe3ec;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 9px;
  margin-bottom: 16px;
  padding: 12px 14px;
}
.notification-option > span { display: grid; gap: 3px; }
.notification-option small { color: #b45309; }
.wizard-actions { display: flex; justify-content: space-between; margin-top: 18px; }
.wizard-actions .btn-primary { margin-left: auto; }
@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
  .reservation-step small { font-size: 11px; text-align: center; }
  .calendar-day { min-height: 50px; }
}
</style>
